import React from 'react';
import * as Sentry from '@sentry/react';
import buildContext from '~/components/jsx-helpers/build-context';
import { useUserModel } from '~/models/usermodel';
import useMyOpenStaxUser from '~/models/myopenstax-user';
import { useRefreshable } from '~/helpers/data';
import debounce from 'lodash/debounce';
const debouncedDebug = debounce((...args) => console.debug(...args), 100);
function checkUserForProblems(user) {
    if (!user || Reflect.ownKeys(user).length === 0) {
        debouncedDebug('No user info retrieved');
    }
    else if (!('pending_verification' in user)) {
        debouncedDebug('No pending_verification flag set in user info', user);
    }
}
function getUserStatus(user) {
    var _a, _b;
    const isInstructor = user.username && 'groups' in user && ((_a = user.groups) === null || _a === void 0 ? void 0 : _a.includes('Faculty'));
    const isStudent = user.username && !isInstructor;
    checkUserForProblems(user);
    return {
        isInstructor,
        isStudent,
        pendingVerification: user.pending_verification,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        userInfo: user,
        school: (_b = user.accountsModel) === null || _b === void 0 ? void 0 : _b.school_name,
        uuid: user.uuid
    };
}
function roleTag({ isInstructor, isStudent }) {
    if (isInstructor) {
        return 'instructor';
    }
    return isStudent ? 'student' : 'anonymous';
}
function useContextValue() {
    var _a;
    const model = useUserModel();
    const userStatus = React.useMemo(() => getUserStatus(model), [model]);
    const isVerified = ((_a = model === null || model === void 0 ? void 0 : model.accountsModel) === null || _a === void 0 ? void 0 : _a.faculty_status) === 'confirmed_faculty';
    const [fetchTime, updateMyOpenStaxUser] = useRefreshable(() => Date.now());
    const myOpenStaxUser = useMyOpenStaxUser(isVerified, fetchTime);
    // Derive login state from userStatus (always available) with fallback to model
    // This is the canonical way to check if a user is logged in
    const isLoggedIn = Boolean((userStatus === null || userStatus === void 0 ? void 0 : userStatus.uuid) || (model === null || model === void 0 ? void 0 : model.uuid));
    const value = React.useMemo(() => model.last_name
        ? {
            accountId: model.id,
            userName: `${model.first_name} ${model.last_name.substr(0, 1)}.`,
            userModel: model,
            uuid: model.uuid,
            isVerified,
            isLoggedIn,
            userStatus,
            myOpenStaxUser,
            updateMyOpenStaxUser
        }
        : { isLoggedIn, userStatus, myOpenStaxUser, uuid: userStatus === null || userStatus === void 0 ? void 0 : userStatus.uuid }, [model, userStatus, isVerified, isLoggedIn, myOpenStaxUser, updateMyOpenStaxUser]);
    React.useEffect(() => {
        var _a;
        const w = window;
        if (model && model.id) {
            (_a = w.pi) === null || _a === void 0 ? void 0 : _a.call(w, 'identify_client', model.id);
        }
    }, [model]);
    React.useEffect(() => {
        // uuid only -- name and email would put PII in every Sentry event
        Sentry.setUser(userStatus.uuid ? { id: userStatus.uuid } : null);
        Sentry.setTag('logged_in', isLoggedIn);
        Sentry.setTag('user_role', roleTag(userStatus));
    }, [userStatus, isLoggedIn]);
    return value;
}
const { useContext, ContextProvider } = buildContext({ useContextValue });
export { useContext as default, ContextProvider as UserContextProvider };
